import type { AISizingDraft } from "@/libs/api/aiEquipmentSuggestions";

type Row = Record<string, unknown>;
export interface PreviewFact { label: string; value: string }
export interface PreviewCard {
  type: string;
  name: string;
  partNumber?: string;
  facts: PreviewFact[];
  housings?: { name: string; facts: PreviewFact[] }[];
}
const record = (value: unknown): Row => value && typeof value === "object" ? value as Row : {};
const text = (value: unknown) => typeof value === "string" && value.trim() ? value.trim() : undefined;
export function previewValue(value: unknown, unit = "", scale = 1): string {
  if (value == null || value === "") return "Not supplied";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "Not supplied";
    return `${(value * scale).toLocaleString("en-US", { maximumFractionDigits: 2 })}${unit ? ` ${unit}` : ""}`;
  }
  return typeof value === "string" ? `${value}${unit ? ` ${unit}` : ""}` : "Not supplied";
}
const fact = (label: string, value: unknown, unit = "", scale = 1): PreviewFact => ({ label, value: previewValue(value, unit, scale) });
const name = (part: Row) => [text(part.manufacturer), text(part.series_model) || text(part.configuration) || text(part.model) || text(part.family) || text(part.description) || text(part.vsd_type) || text(part.transformer_type)].filter(Boolean).join(" / ") || text(part.part_number) || "Selected equipment";

/** Presentation only: use selected catalog/xref data, never synthesize engineering metrics. */
export function equipmentPreviewDetails(draft: AISizingDraft, parameters: Row) {
  const equipment = draft.equipment;
  const card = (type: string, part: Row, facts: PreviewFact[]): PreviewCard => ({ type, name: name(part), partNumber: text(part.part_number), facts });
  const dimensions = (part: Row) => [fact("Length", part.length_inches, "ft", 1 / 12), fact("Weight", part.weight_pounds, "lb"), fact("Outside diameter", part.outer_diameter_inches, "in")];
  const ordered = (type: "pumps" | "intakes" | "seals" | "cables") =>
    (Array.isArray(equipment[`${type}_order`]) ? equipment[`${type}_order`] as string[] : []).map(id => draft[type][id]).filter(Boolean);
  const pumps = ordered("pumps").map((row, i) => {
    const part = record(row.r_pump);
    const result = card(`Pump ${i + 1}`, part, [fact("Selected stages", row.total_stages), fact("Length", row.length_feet, "ft"), fact("Weight", row.weight_pounds, "lb"), fact("High-strength shaft", row.use_high_strength_shaft), fact("Construction", part.construction), fact("Housing diameter", part.housing_outer_diameter_inches, "in")]);
    const ids = Array.isArray(row.housings_order) ? row.housings_order as string[] : [];
    result.housings = ids.map(id => draft.housings[id]).filter(Boolean).map(housing => {
      const catalog = record(housing.r_housing);
      return { name: `Housing ${catalog.housing_number ?? "—"}`, facts: [fact("Stages", housing.stage_count ?? catalog.stage_count), fact("Length", housing.length_inches ?? catalog.length_inches, "ft", 1 / 12), fact("Weight", catalog.weight_pounds, "lb")] };
    });
    return result;
  });
  const intakes = ordered("intakes").map((row, i) => {
    const part = record(row.r_intake);
    return card(`Intake ${i + 1}`, part, [fact("Type", part.intake_type), ...dimensions(part), fact("Maximum rate", part.max_rate_bpd, "BPD"), fact("High-strength shaft", row.use_high_strength_shaft)]);
  });
  const motor = record(equipment.r_motor);
  const seals = ordered("seals").map((row, i) => {
    const part = record(row.r_seal);
    return card(`Seal ${i + 1}`, part, [...dimensions(part), fact("Housing material", part.housing_material), fact("Bearing", part.bearing_type), fact("Temperature rating", part.max_temperature_fahrenheit, "°F"), fact("High-strength shaft", row.use_high_strength_shaft)]);
  });
  if (equipment.r_sensor) {
    const part = record(equipment.r_sensor);
    seals.push(card("Sensor", part, [...dimensions(part), fact("Channels", part.channels)]));
  }
  if (equipment.r_mle) {
    const part = record(equipment.r_mle);
    seals.push(card("Motor lead extension", part, [fact("Series", part.series), fact("Gauge", part.gauge, "AWG"), fact("Voltage rating", part.kv, "kV"), fact("Length", part.length_feet, "ft")]));
  }
  const cables = ordered("cables").map((row, i) => {
    const part = record(row.r_cable);
    return card(`Cable segment ${i + 1}`, part, [fact("Installed length", row.length_feet, "ft"), fact("Gauge", part.gauge, "AWG"), fact("Voltage rating", part.kv, "kV"), fact("Construction", part.construction), fact("Armor", part.armor)]);
  });
  const surface: PreviewCard[] = [];
  for (const [type, relation] of [equipment.use_vsd ? ["Variable-speed drive", "r_vsd"] : ["Switchboard", "r_switchboard"], ["Step-up transformer", "r_step_up_transformer"], ["Step-down transformer", "r_step_down_transformer"]]) {
    if (!equipment[relation]) continue;
    const part = record(equipment[relation]);
    const facts = relation.includes("transformer") ? [fact("Rating", part.kva, "kVA"), fact("Primary voltage", part.primary_voltage ?? part.primary_voltage_delta, "V"), fact("Minimum secondary voltage", part.min_secondary_voltage, "V"), fact("Maximum secondary voltage", part.max_secondary_voltage, "V")]
      : [fact("Current rating", part.max_amps ?? part.amps, "A"), fact("Rating", part.kva, "kVA"), fact("Voltage rating", part.kv, "kV")];
    surface.push(card(type, part, facts));
  }
  return [
    [card("Operating point", {}, [fact("Target production", parameters.target_liquid_rate_std_barrel_day, "STB/D"), fact("Operating frequency", parameters.pump_hz, "Hz"), fact("Minimum frequency", parameters.minimum_frequency, "Hz"), fact("Maximum frequency", parameters.maximum_frequency, "Hz"), fact("Drive", equipment.use_vsd ? "Variable-speed drive" : "Switchboard")])].map(item => ({ ...item, name: "Recommended operating settings" })),
    intakes, pumps,
    equipment.r_motor ? [card("Motor", motor, [fact("Nameplate power", motor.nameplate_horsepower, "HP"), fact("Nameplate voltage", motor.nameplate_voltage, "V"), fact("Nameplate current", motor.nameplate_current, "A"), ...dimensions(motor), fact("Temperature rating", motor.temperature_rating_fahrenheit, "°F")])] : [],
    seals, cables, surface
  ];
}
